import { useCallback, useState } from "react";

export type TableSortOrder = "asc" | "desc" | undefined;

const NEXT_ORDER = { asc: "desc", desc: undefined, undefined: "asc" } as const;

export const nextSortOrder = (order: TableSortOrder): TableSortOrder =>
  NEXT_ORDER[order ?? "undefined"];

export type TableSortValues<T> = Record<
  string,
  (row: T) => string | number | undefined | null
>;

export type TableSortState = {
  sortBy: string | undefined;
  sortOrder: TableSortOrder;
  toggleSort: (key: string) => void;
  orderFor: (key: string) => TableSortOrder | null;
};

type UseTableSortOptions = {
  defaultSortBy?: string;
  defaultSortOrder?: TableSortOrder;
  onSortChange?: () => void;
};

export function useTableSort({
  defaultSortBy,
  defaultSortOrder,
  onSortChange,
}: UseTableSortOptions = {}): TableSortState {
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<TableSortOrder>(defaultSortOrder);

  const toggleSort = useCallback(
    (key: string) => {
      setSortBy(key);
      setSortOrder((previous) =>
        key === sortBy ? nextSortOrder(previous) : "asc"
      );
      onSortChange?.();
    },
    [sortBy, onSortChange]
  );

  const orderFor = useCallback(
    (key: string) => (sortBy === key ? sortOrder : null),
    [sortBy, sortOrder]
  );

  return { sortBy, sortOrder, toggleSort, orderFor };
}

export function sortRows<T>(
  rows: T[],
  values: TableSortValues<T>,
  { sortBy, sortOrder }: Pick<TableSortState, "sortBy" | "sortOrder">
): T[] {
  const getValue = sortBy ? values[sortBy] : undefined;
  if (!getValue || !sortOrder) return rows;

  const direction = sortOrder === "asc" ? 1 : -1;
  return [...rows].sort((rowA, rowB) => {
    const [left, right] = [getValue(rowA), getValue(rowB)];
    if (left === right) return 0;
    if (left === undefined || left === null) return 1;
    if (right === undefined || right === null) return -1;
    return (left < right ? -1 : 1) * direction;
  });
}
